/**
 * 이상한 방 - 걸기 배너 ("OO이 걸었다!" 전원 1초, 순수 DOM)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 5절(3번 - 말싸움을 규칙으로 · "야 저거 맞잖아!!"가 게임의 재미다)
 *            `이상한방-상태모델-v0.1.md` 원칙 2(UI가 덮은 면적은 곧 볼 수 없는 방의 넓이)
 *            로드맵 M7(`wagerBanner` - 매치에서 "OO이 걸었다!" 전원 배너 1초, 페이즈 자막 규약)
 *
 * ## 왜 전원에게 가는가
 *
 * 걸기는 혼자 하는 선언이 아니다. 누가 판을 키웠다는 사실이 방 전체에 보여야 "저 사람 지금 확신하네"가 되고, 그것이
 * 컨셉시트 5절의 말싸움을 화면이 대신하는 방법이다. 호스트가 `raised{by}`를 `'all'`에 보내는 이유가 이것이고
 * (`matchShoot.onRaise`), 이 배너는 그 효과 하나를 이름 한 줄로 바꾼다. 이름은 사전을 거치지 않는다(이름표와 같다).
 *
 * ## 페이즈 자막 규약을 따른다 - 다만 시계는 잡지 않는다
 *
 * `juice.ts`의 페이즈 자막과 같은 규약이다: **타이머 하나가 정리한다**, `animationend`를 듣지 않는다(움직임 줄이기에서는
 * 그 이벤트가 안 와 노드가 영원히 남는다), 앞의 것이 떠 있으면 먼저 걷는다. 다른 것은 하나다 - 이 배너는 **시계를 잡지 않는다.**
 * 페이즈 자막이 시계를 잡는 이유는 그동안 아무도 쏠 수 없어서인데, 걸기는 수색 한가운데의 사건이라 남들은 계속 쏘고 있다.
 * 1초 동안 시계를 멈추면 걸기가 곧 "전원 1초 정지" 버튼이 된다.
 *
 * ## 자리: 위쪽 가운데, 방을 덮지 않는다
 *
 * 페이즈 자막의 자리(가운데)와 같은 축에 서되 워시를 깔지 않고, 얇은 노란 판 하나가 페이즈 시계 아래에 잠깐 선다.
 * `pointer-events: none` - 배너 위로 탭해도 총은 방을 향해 나간다. 1초는 수색 중 자막의 상한(`juice.ts`의 0.5초)보다 길지만
 * 이 배너는 **남의 사건**이고 화면 중앙을 덮지 않으므로 그 규칙의 대상이 아니다(같은 이유로 남의 이름표도 예외다).
 */

import { t } from '../i18n'

import { createElement, restartAnimation, setHidden, setText } from './dom'
import { overlayLayout } from './overlayLayout'

import './wagerBanner.css'

/* --- 길이 ---------------------------------------------------------------------- */

/** 배너가 화면에 머무는 시간(ms). 로드맵 M7이 정한 1초 - 이름 하나를 읽을 만큼이고, 그 이상은 방을 가린다. */
export const WAGER_BANNER_MS = 1000

/* --- 타입 ---------------------------------------------------------------------- */

export type WagerBannerHandle = Readonly<{
  root: HTMLElement
  /**
   * "{name}이 걸었다!"를 1초 띄운다. 앞의 것이 떠 있으면 걷고 처음부터 다시 돈다.
   * `mine`이면 "내가 걸었다!"다 - 내 좌석 이름('나')을 이름 자리에 넣으면 '나가 걸었다!'가 된다(조사 조립은 이름을 3인칭으로 본다).
   */
  show: (name: string, mine?: boolean) => void
  /** 지금 떠 있는 것을 즉시 걷는다. 라운드가 접히거나 세션이 잠들 때. */
  clear: () => void
  /** 배치 모드·잠든 무대에서는 통째로 접는다(HUD의 `setVisible`과 같은 시점). 접히면 떠 있던 것도 걷는다. */
  setVisible: (visible: boolean) => void
  dispose: () => void
}>

export type WagerBannerOptions = Readonly<{ mount: HTMLElement }>

/* --- 본체 ---------------------------------------------------------------------- */

export function createWagerBanner(options: WagerBannerOptions): WagerBannerHandle {
  const root = createElement('div', 'wb-root')
  root.setAttribute('role', 'status')
  root.hidden = true

  const plate = createElement('p', 'wb-plate', '')
  root.append(plate)
  // 가운데 알림 열의 셋째 칸(`overlayLayout.ts`).
  const layout = overlayLayout(options.mount)
  layout.place(root, 'wager')

  /** 정리의 예약. 권한은 이 하나뿐이다(`juice.ts` 규약). */
  let timer: number | null = null
  let visible = true

  function clear(): void {
    if (timer !== null) {
      window.clearTimeout(timer)
      timer = null
    }
    root.classList.remove('is-on')
    setHidden(root, true)
  }

  return {
    root,

    show(name: string, mine = false): void {
      // 앞의 것을 먼저 걷는다. 둘이 겹치면 둘 다 안 읽히고, 타이머가 둘이면 먼저 끝나는 쪽이 뒤의 것을 자른다.
      clear()
      // 잠든 무대에서는 띄우지 않는다. 걸기는 시계를 잡지 않으므로(머리말) 생략해도 게임이 멈추지 않는다.
      if (!visible) return

      setText(plate, mine ? t('wager.banner.self') : t('wager.banner', { name }))
      setHidden(root, false)
      // 클래스가 없는 상태로 한 번 플러시해야 애니메이션이 처음부터 돈다(`ui/dom.restartAnimation`).
      restartAnimation(root)
      root.classList.add('is-on')
      timer = window.setTimeout(clear, WAGER_BANNER_MS)
    },

    clear,

    setVisible(next: boolean): void {
      visible = next
      if (!next) clear()
    },

    dispose(): void {
      clear()
      // 기둥에서 걷는다 - `remove()`만 하면 관측기가 떼어 낸 노드를 계속 붙든다.
      layout.unplace(root)
    },
  }
}
